// The server's native fetch, captured before Next.js patches globalThis.fetch.
//
// On the Node server Next replaces globalThis.fetch with
// `createPatchedFetcher(createDedupeFetch(globalThis.fetch))`. For every SSR
// request the dedupe layer tees the response body (clone-response.ts → body.tee())
// and holds the never-consumed clone in a per-request React cache. A teed
// ReadableStream keeps buffering until both branches are read/cancelled, so the
// unread clone pins native buffers; under sustained traffic these locked streams
// accumulate faster than GC reclaims them and the process OOMs. Reading
// cookies()/headers() forces dynamic rendering, so matrix hits this path on
// every request.
//
// Next patches fetch lazily, per render (app-render `patchFetch()`), whereas
// `instrumentation.ts` register() runs once at server startup, before any
// request. Capturing globalThis.fetch there yields the genuine native fetch (the
// exact implementation Next would otherwise wrap), which does NOT tee. Routing ky
// through it on the server removes the retention path while preserving behaviour
// — it is the same fetch, minus Next's patch.
//
// Refs: https://github.com/vercel/next.js/issues/64212 (unpatched fetch does not
// leak), https://github.com/vercel/next.js/issues/85914 (same class of fix).

// Global registry symbol so the capture (instrumentation) and the read
// (executeFetch) resolve the same slot even when bundled into separate chunks.
const UNPATCHED_FETCH_KEY = Symbol.for('@unifyapps/network/unpatchedFetch');

interface GlobalWithUnpatchedFetch {
  [UNPATCHED_FETCH_KEY]?: typeof fetch;
}

/**
 * Capture the native fetch. Call once from instrumentation register() on the
 * Node runtime, before any render triggers Next's fetch patching. Idempotent.
 */
export function captureUnpatchedFetch(): void {
  const globalRef = globalThis as GlobalWithUnpatchedFetch;
  if (!globalRef[UNPATCHED_FETCH_KEY] && typeof globalThis.fetch === 'function') {
    globalRef[UNPATCHED_FETCH_KEY] = globalThis.fetch.bind(globalThis);
  }
}

/**
 * The captured native fetch, or undefined when no capture exists (client, React
 * Native, or a server that has not opted in via instrumentation). Callers fall
 * back to the platform default fetch when this is undefined.
 */
export function getServerFetch(): typeof fetch | undefined {
  return (globalThis as GlobalWithUnpatchedFetch)[UNPATCHED_FETCH_KEY];
}
